import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import defaultCountries from 'data/containers/countries';

import CountryPreview from 'components/CountryPreview';
import { filterOptions } from 'components/Filters/utils/filterOptions';
import { mapOptionsWithLabels } from 'components/Filters/utils/mapOptionsWithLabels';

import { ElasticSearchFacetCounts } from 'types/commons';
import { FormattedOption } from 'types/dictionaries';

import SelectCommon from '../common/SelectCommon/SelectCommon';
import { SelectValue } from '../filter.types';

const { Option } = Select;

interface CountrySelectProps {
  title: string;
  value: SelectValue;
  onChange: (value: SelectValue | undefined) => void;
  countries?: { code: string; label: string }[];
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hasAccessToFilter?: boolean;
  facets?: ElasticSearchFacetCounts;
}

const CountrySelect: FC<CountrySelectProps> = ({
  title,
  value,
  onChange,
  countries = defaultCountries,
  isLoading = false,
  placeholder,
  className,
  disabled = false,
  hasAccessToFilter = false,
  facets
}) => {
  const { t } = useTranslation();

  const countryPlaceholder = placeholder ?? t('common.label.enterCountry');

  const filteredAndFormattedOptions = useMemo((): FormattedOption[] => {
    const base = countries.map((country) => ({
      value: country.code,
      label: country.label
    }));

    const normalizedValue = Array.isArray(value)
      ? value
      : value
        ? [value]
        : undefined;

    return filterOptions(
      normalizedValue,
      mapOptionsWithLabels(base, facets),
      facets
    );
  }, [countries, facets, value]);

  const options = filteredAndFormattedOptions.map((country) => (
    <Option key={country.value} value={country.value}>
      <CountryPreview
        countryCode={country.value}
        countryName={country.label as string}
      />
    </Option>
  ));

  const filterByCountryName = (input: string, option?: DefaultOptionType) => {
    return option?.props.children.props.countryName
      .toLowerCase()
      .includes(input.toLowerCase());
  };

  if (facets && filteredAndFormattedOptions.length === 0) {
    return null;
  }

  return (
    <SelectCommon
      title={title}
      value={value}
      placeholder={countryPlaceholder}
      customOptions={options}
      onChange={onChange}
      filterOption={filterByCountryName}
      optionFilterProp="children"
      loading={isLoading}
      className={className}
      disabled={disabled}
      hasAccessToFilter={hasAccessToFilter}
    />
  );
};

export default CountrySelect;
